import { EntityId, PagedTenant, RequestAvailableSlots, Tenant, TenantComment } from "@models";
import { tenantService } from "@services";
import { makeAutoObservable, runInAction } from "mobx";

export default class TenantStore {
  tenants = new Map<number, Tenant>();
  tenant: Tenant | undefined;
  totalCount = 0;
  maxResultCount = 10;
  /**
   * Koliko je stavki servis vratio za tekući filter. Ujedno je i `skipCount`
   * sledeće strane — namerno se ne računa iz `tenants.size`, jer mapa uklanja
   * duplikate pa bi se paginacija zaglavila na istoj strani.
   */
  loadedCount = 0;
  loading = false;
  /**
   * Da li je poslednji `getAll` pukao. Bez ovoga se prazna lista posle pada
   * API-ja ne razlikuje od kategorije koja stvarno nema salone — a `Seo` na
   * osnovu te razlike odlučuje da li stranicu poslati u `noindex`.
   */
  loadFailed = false;
  loadingMore = false;
  /**
   * Prvo učitavanje tekuće kategorije je završeno (uspešno ili ne). Stoji u
   * store-u, a ne u `useRef`, da bi promena stvarno prerenderovala scenu —
   * o njoj zavisi da li `Seo` šalje stranicu u `noindex`.
   */
  loadedOnce = false;

  availableTimeSlots: string[] = [];
  availableTimeSlotsLoading: boolean = false;
  availableTimeSlotsScopeKey: string | undefined = undefined;

  comments: TenantComment[] = [];
  commentsTotalCount = 0;
  commentsMaxResultCount = 10;
  commentsLoading = false;
  commentsLoadingMore = false;

  // Guards against out-of-order getAll responses: a slower earlier request
  // (e.g. the pre-town-load "all tenants" fetch) must not overwrite the
  // results of a newer filtered request. Non-observable on purpose.
  getAllSeq = 0;

  constructor() {
    makeAutoObservable(this, { getAllSeq: false });
  }

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setLoadingMore = (value: boolean) => {
    runInAction(() => {
      this.loadingMore = value;
    });
  };

  setAvailabilityLoading = (value: boolean) => {
    runInAction(() => {
      this.availableTimeSlotsLoading = value;
    });
  };

  /** `append` je za dugme "Prikaži još": već učitane firme ostaju, dodaje se sledeća strana. */
  getAll = async (request: PagedTenant, append = false) => {
    const seq = ++this.getAllSeq;
    append ? this.setLoadingMore(true) : this.setLoading(true);
    try {
      const response = await tenantService.getAll(request);
      if (seq !== this.getAllSeq) return; // superseded by a newer request
      runInAction(() => {
        if (!append) {
          this.tenants.clear();
          this.loadedCount = 0;
        }
        response.items.forEach((tenant: Tenant) => {
          this.tenants.set(tenant.id, tenant);
        });
        this.loadedCount += response.items.length;
        // Prazna strana znači da je spisak iscrpljen bez obzira na to šta
        // `totalCount` tvrdi — bez ovoga dugme "Učitaj još" ostaje zauvek.
        this.totalCount =
          append && response.items.length === 0 ? this.loadedCount : response.totalCount;
        this.loading = false;
        this.loadingMore = false;
        this.loadFailed = false;
        this.loadedOnce = true;
      });
    } catch (error) {
      if (seq !== this.getAllSeq) return;
      console.error(error);
      runInAction(() => {
        this.loading = false;
        this.loadingMore = false;
        this.loadedOnce = true;
        // Pad pri dopuni ne sme da obeleži celu stranicu kao neuspelu — prva
        // strana je već prikazana i validna za indeksiranje.
        if (!append) this.loadFailed = true;
      });
    }
  };

  getAvailableSlots = async (request: RequestAvailableSlots) => {
    const key = `${request.userId}|${request.tenantId}|${request.date}|${request.requiredMinutes}`;
    if (this.availableTimeSlotsScopeKey === key) return;
    this.setAvailabilityLoading(true);
    runInAction(() => {
      this.availableTimeSlotsScopeKey = key;
      this.availableTimeSlots = [];
    });
    try {
      const response = await tenantService.getAvailableSlots(request);
      runInAction(() => {
        if (this.availableTimeSlotsScopeKey !== key) return;
        this.availableTimeSlots = response;
        this.availableTimeSlotsLoading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        if (this.availableTimeSlotsScopeKey !== key) return;
        this.availableTimeSlotsScopeKey = undefined;
        this.availableTimeSlotsLoading = false;
      });
    }
  };

  get = async (id: EntityId) => {
    this.setLoading(true);
    try {
      const response = await tenantService.get(id);
      runInAction(() => {
        this.tenant = response;
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  };

  resetTenant = () => {
    this.tenant = undefined;
  };

  clearTenants = () => {
    this.tenants.clear();
    this.totalCount = 0;
    this.loadedCount = 0;
    this.loadedOnce = false;
    this.loadFailed = false;
  };

  clearTenant = () => {
    this.tenant = undefined;
  };

  clearTimeSlots = () => {
    runInAction(() => {
      this.availableTimeSlots = [];
      this.availableTimeSlotsScopeKey = undefined;
    });
  };

  getComments = async (tenantId: number, skipCount: number, maxResultCount: number, append: boolean) => {
    append ? this.setCommentsLoadingMore(true) : this.setCommentsLoading(true);
    try {
      const response = await tenantService.getComments(tenantId, skipCount, maxResultCount);
      runInAction(() => {
        this.comments = append ? [...this.comments, ...response.items] : response.items;
        this.commentsTotalCount = response.totalCount;
        append ? (this.commentsLoadingMore = false) : (this.commentsLoading = false);
      });
    } catch (error) {
      console.error(error);
      append ? this.setCommentsLoadingMore(false) : this.setCommentsLoading(false);
    }
  };

  setCommentsLoading = (value: boolean) => {
    runInAction(() => {
      this.commentsLoading = value;
    });
  };

  setCommentsLoadingMore = (value: boolean) => {
    runInAction(() => {
      this.commentsLoadingMore = value;
    });
  };

  clearComments = () => {
    this.comments = [];
    this.commentsTotalCount = 0;
  };
}
